import { supabase } from '../lib/supabase';
import type { SearchFilters } from '../types';

// Define valid lead types
export type LeadType = 
  | 'phone_click'
  | 'website_visit'
  | 'profile_view'
  | 'registration'
  | 'service_click'
  | 'quote_request';

// Define valid conversion types
export type ConversionType =
  | 'phone_click'
  | 'website_visit'
  | 'registration'
  | 'service_click'
  | 'quote_request';

export async function trackSearch(zipCode: string, resultsCount: number, filters: SearchFilters = {}) {
  try {
    if (!zipCode) return;
    
    // Get city and service info
    const { data: cityData, error: cityError } = await supabase
      .from('cities')
      .select(`
        id,
        name,
        state:state_id(code)
      `)
      .filter('zip_codes', 'cs', `{${zipCode}}`)
      .single();

    if (cityError) {
      console.error('Error getting city info:', cityError);
    }

    const { error: searchError } = await supabase
      .from('analytics_searches')
      .insert({
        query: zipCode,
        zip_code: zipCode,
        city_id: cityData?.id,
        filters,
        results_count: resultsCount,
        metadata: {
          city_name: cityData?.name,
          state_code: cityData?.state?.code,
          filter_services: filters.services || [],
          filter_rating: filters.rating,
          filter_verified: filters.verified,
          filter_featured: filters.featured,
          filter_sort: filters.sortBy,
          timestamp: new Date().toISOString()
        }
      });

    if (searchError) throw searchError;
  } catch (error) {
    console.error('Error tracking search:', error);
  }
}

export async function trackLead(type: LeadType, metadata: Record<string, any> = {}) {
  try {
    if (!type) return;

    // Validate UUID fields
    const {
      plumber_id,
      city_id,
      service_id,
      business_name,
      service_slug,
      ...otherMetadata
    } = metadata;

    // Get service ID from slug if provided
    let validServiceId = service_id;
    if (service_slug && !service_id) {
      const { data: serviceData } = await supabase
        .from('services')
        .select('id')
        .eq('slug', service_slug)
        .single();
      validServiceId = serviceData?.id;
    }

    const { error } = await supabase
      .from('analytics_leads')
      .insert({
        type,
        plumber_id: plumber_id || null,
        city_id: city_id || null,
        service_id: validServiceId || null,
        metadata: {
          ...otherMetadata,
          business_name,
          service_slug,
          timestamp: new Date().toISOString()
        },
        created_at: new Date().toISOString()
      });

    if (error) throw error;
  } catch (error) {
    console.error('Error tracking lead:', error);
  }
}

export async function trackConversion(type: ConversionType, metadata: Record<string, any> = {}) {
  try {
    if (!type) return;

    const {
      value,
      plumber_id,
      service_id,
      city_id,
      business_name,
      service_slug,
      ...otherMetadata
    } = metadata;

    // Get service ID from slug if provided
    let validServiceId = service_id;
    if (service_slug && !service_id) {
      const { data: serviceData } = await supabase
        .from('services')
        .select('id')
        .eq('slug', service_slug)
        .single();
      validServiceId = serviceData?.id;
    }

    const { error } = await supabase
      .from('analytics_conversions')
      .insert({
        type,
        plumber_id: plumber_id || null,
        service_id: validServiceId || null,
        city_id: city_id || null,
        value: value || 0,
        metadata: {
          ...otherMetadata,
          business_name,
          service_slug,
          timestamp: new Date().toISOString()
        },
        created_at: new Date().toISOString()
      });

    if (error) throw error;
  } catch (error) {
    console.error('Error tracking conversion:', error);
  }
}